import cx from 'classnames';
import { loginOut } from '@/api/user';
import styles from '../index.module.scss';
import Tooltip from '@/components/CustomTooltip';
import useCheckLoginState from '@/hooks/useCheckLoginState';
import More from '../../../assets/icon/homeSidebar/more.svg';
import CustomMenu, { MenuItem } from '@/components/CustomMenu';
import DjiQR from '../../../assets/icon/homeSidebar/wxQRCode.svg';
import { ActionTypeValues, namespace, StoreContext } from '@/context';
import { FC, useContext, useEffect, useMemo, useRef, useState } from 'react';
import DjiWpQR from '../../../assets/icon/homeSidebar/wp.svg';

interface SideBarFootorProps {
  disableResponsive?: boolean;
}

// 底部<更多>垂直菜单
const SideBarFootor: FC<SideBarFootorProps> = ({ disableResponsive = false }) => {
  const { state, dispatch } = useContext(StoreContext);
  const { track, TRACK_EVENTS } = state[namespace.app].sensors || {};
  const { pcActiveChannelName } = state[namespace.home];
  // 绑定垂直菜单容器
  const menuRef = useRef<HTMLDivElement>(null);
  // 更多按钮
  const moreDivRef = useRef<HTMLDivElement>(null);
  // 展开垂直菜单状态值
  const [popoverVisible, setPopoverVisible] = useState(false);
  // 点击菜单容器以外关闭垂直菜单
  const handleClickOutside = (event: MouseEvent) => {
    if (menuRef.current && !menuRef.current.contains(event.target as Node) && !moreDivRef.current?.contains(event.target as Node)) {
      setPopoverVisible(false);
    }
  };

  const { isLogout } = useCheckLoginState();

  const handleStopPropagation= (e: React.MouseEvent) => {
    e.stopPropagation(); // 阻止事件冒泡
  };

  const addOnClickToLinks = (menuItems: MenuItem[]): MenuItem[] =>{
    return menuItems.map((item) => {
      if (item.link) {
        return {
          ...item,
          onClick: () => {
            setPopoverVisible(false);
            track?.(TRACK_EVENTS?.[namespace.home].serviceMore.name, {
              list_name: pcActiveChannelName,
              button_name: item.title,
              url: item.link,
              position: 'More'
            }, namespace.home);
            window.open(item.link, '_blank');
          }
        };
      } else if (item.children) {
        return {
          ...item,
          children: addOnClickToLinks(item.children)
        };
      }
      return item;
    });
  }

  const options: MenuItem[] = useMemo(() => {
    const opts: MenuItem[] = [
      {
        key: 'aboutDJICommunity',
        title: '关于大疆社区',
        children: [
          {
            key: 'communityRules',
            title: '社区规则',
            link: 'https://bbs.dji.com/pro/detail?tid=23',
          },
          {
            key: 'privacyAgreement',
            title: '隐私权协议',
            link: 'https://www.dji.com/cn/policy?site=brandsite&from=bbs',
            divider: true,
          },
          {
            key: 'fileNumber',
            title: (
              <div
                className={styles.recordedInfo}
                onClick={() => {
                  setPopoverVisible(false);
                  window.open('https://beian.miit.gov.cn/', '_blank');
                }}
              >
                {`Copyright@${new Date().getFullYear()} 大疆社区 版权所有 粤ICP备2022092332号`}
              </div>
            ),
            disabled: true,
          }
        ],
        divider: true,
      },
      {
        key: 'focus',
        title: (
          <div className={styles.focusInfo}>
            <div className={styles.menuTitle}>关注我们</div>
            <div className={styles.focusContent}>
              <div className={cx(styles.wechatIcon, styles.focusIcon)}></div>
              <div className={cx(styles.wxQRCode)}>
                <DjiQR />
                <div className={styles.desc}>微信扫码</div>
                <div className={styles.desc}>关注官方公众号</div>
              </div>
              <div className={cx(styles.weiboIcon, styles.focusIcon)}></div>
              <div className={cx(styles.wxCode)}>
                <DjiWpQR />
                <div className={styles.desc}>微信扫码</div>
                <div className={styles.desc}>探索大疆官方小程序</div>
              </div>
              <Tooltip
                onClick={() => {
                  window.open('https://www.xiaohongshu.com/user/profile/64a29c5a000000000a023d16?xsec_token=AByeDayXDbS89UqMu2D34XtRN5lrhpmQQdysOa91It-as=&xsec_source=pc_search', '_blank');
                }}
                placement='top'
                title={'前往大疆社区官方小红书'}
                className={cx(styles.redBookIcon, styles.focusIcon)}
              />
            </div>
          </div>
        ),
        disabled: true,
        divider: true,
      },
      {
        key: 'recommendedSites',
        title: (
          <div className={styles.menuTitle}>
            推荐站点
          </div>
        ),
        disabled: true,
      },
      {
        key: 'DJIofficialWebsite',
        title: '大疆官网',
        link: 'https://www.dji.com?site=bbs_pro&from=sidebar_recommend',
      },
      {
        key: 'DJIOfficialMall',
        title: '大疆商城',
        link: 'https://store.dji.com/cn?site=bbs_pro&from=sidebar_recommend',
      },
      {
        key: 'skyCity',
        title: '天空之城',
        link: 'https://www.skypixel.com/?site=bbs_pro&from=sidebar_recommend',
      },
      {
        key: 'more',
        title: '更多',
        children: [
          {
            key: 'support',
            title: '服务与支持',
            link: 'https://www.dji.com/cn/support?site=bbs_pro&from=sidebar_more',
          },
          {
            key: 'officialPeripherals',
            title: '官方周边',
            link: 'https://www.dji.com/cn/souvenir?site=bbs_pro&from=sidebar_more'
          },
          {
            key: 'media',
            title: '媒体中心',
            link: 'https://www.dji.com/cn/media-center?site=bbs_pro&from=sidebar_more',
          },
          {
            key: 'offer',
            title: '招聘精英',
            link: 'https://we.dji.com/zh-CN',
          },
          {
            key: 'deviceSearch',
            title: '设备查询',
            link: 'https://repair.dji.com/device/search?site=bbs_pro&from=sidebar_more&re=CN&lang=zh-CN',
          },
          {
            key: 'online',
            title: '线上寄修',
            link: 'https://repair.dji.com/cn/repair/index?site=bbs_pro&from=sidebar_more',
          },
          {
            key: 'safe',
            title: '安全飞行指引',
            link: 'https://fly-safe.dji.com/home?site=bbs_pro&from=sidebar_more',
          },
        ],
        divider: !isLogout,
      },
    ]
    if (!isLogout) {
      opts.push({
        key: 'logout',
        title: '退出登录',
        onClick: () => {
          loginOut().then(()=>{
            dispatch({
              namespace: namespace.app,
              type: ActionTypeValues.CLEAR_ACCOUNT
            });
            setPopoverVisible(false);
            const redirectPaths = ['/pro/edit', '/pro/message', '/pro/points'];
            if (redirectPaths.some(path => window.location.pathname.startsWith(path))) {
              window.location.href = '/';
            } else {
              window.location.reload();
            }
          });
        },
        hideHoverIcon: true,
      },)
    }
    return opts;
  }, [isLogout])

  

  useEffect(() => {
    if (popoverVisible) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [popoverVisible]);

  return (
    <div className={styles.footer} onClick={() => {setPopoverVisible(!popoverVisible)}}>
       {popoverVisible && (
          <div
            onClick={handleStopPropagation}
            ref={menuRef}
          >
            <CustomMenu options={addOnClickToLinks(options)} />
          </div>
        )}
        <div className={styles.footerContent}>
         <div className={styles.footerButton} style={disableResponsive ? { width: '100%', height: 'auto', padding: '8px 12px' } : undefined}>
            {/* tooltip */}
            <div className={cx(styles.tooltip, styles.tooltipRight)} data-tooltip="更多" >
              {/* icon  */}
              <div className={styles.icon} style={disableResponsive ? { marginRight: 10 } : undefined}>
                <More />
              </div>
            </div>
            <div ref={moreDivRef} className={cx(styles.footerMoreBtn)} style={disableResponsive ? { display: 'block' } : undefined}>更多</div>
         </div>
        </div>
    </div>
  )
}

export default SideBarFootor;
